import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Users, Code, Target, Zap, CheckCircle2, Award,
  Globe, Clock, Lock, TrendingUp, Layers, Database, LineChart
} from 'lucide-react';

const HireAIDevelopers = () => {
  const expertiseAreas = [
    {
      icon: Target,
      title: 'Machine Learning Engineers',
      description: 'Expert ML engineers specializing in TensorFlow, PyTorch, scikit-learn',
      skills: ['Deep Learning', 'Neural Networks', 'Model Optimization', 'ML Ops']
    },
    {
      icon: Code,
      title: 'AI Software Engineers',
      description: 'Full-stack AI engineers building production-ready AI applications',
      skills: ['Python', 'FastAPI', 'React', 'System Architecture']
    },
    {
      icon: Zap,
      title: 'LLM Integration Specialists',
      description: 'Experts in GPT-4, Claude, LLaMA, and custom LLM implementations',
      skills: ['OpenAI API', 'LangChain', 'Prompt Engineering', 'RAG Systems']
    },
    {
      icon: Database,
      title: 'Data Engineers',
      description: 'Build robust data pipelines and vector databases for AI systems',
      skills: ['ETL Pipelines', 'Vector DBs', 'Data Modeling', 'Real-time Processing']
    },
    {
      icon: LineChart,
      title: 'MLOps Engineers',
      description: 'Deploy, monitor, and scale ML models in production environments',
      skills: ['Kubernetes', 'Docker', 'CI/CD', 'Model Monitoring']
    },
    {
      icon: Lock,
      title: 'AI Security Specialists',
      description: 'Ensure AI systems are secure, compliant, and ethically aligned',
      skills: ['AI Governance', 'Data Privacy', 'Model Security', 'Compliance']
    }
  ];

  const engagementModels = [
    {
      icon: Users,
      title: 'Dedicated AI Team',
      description: 'Full-time team working exclusively on your project',
      features: [
        'Hand-picked senior engineers',
        '100% focus on your project',
        'Direct communication & collaboration',
        'Flexible team scaling'
      ],
      bestFor: 'Long-term AI initiatives requiring dedicated focus'
    },
    {
      icon: Layers,
      title: 'Staff Augmentation',
      description: 'Add AI expertise to your existing development team',
      features: [
        'Integrate with your team',
        'Fill specific skill gaps',
        'Flexible engagement duration',
        'Knowledge transfer included'
      ],
      bestFor: 'Enhancing existing teams with AI capabilities'
    },
    {
      icon: Target,
      title: 'Project-Based Hiring',
      description: 'Hire developers for specific AI projects or deliverables',
      features: [
        'Fixed scope & timeline',
        'Milestone-based delivery',
        'Complete project ownership',
        'Post-launch support'
      ],
      bestFor: 'Defined AI projects with clear objectives'
    }
  ];

  const hiringProcess = [
    {
      step: '01',
      title: 'Share Requirements',
      description: 'Tell us about your AI project, required skills, and timeline',
      duration: '24 hours'
    },
    {
      step: '02',
      title: 'Developer Matching',
      description: 'We match you with pre-vetted senior AI developers',
      duration: '48 hours'
    },
    {
      step: '03',
      title: 'Interview & Selection',
      description: 'Interview shortlisted developers and select your team',
      duration: '3-5 days'
    },
    {
      step: '04',
      title: 'Onboarding & Kickoff',
      description: 'Developers onboard and project kickoff within days',
      duration: '1-2 weeks'
    }
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: 'Senior Engineers Only',
      description: '5+ years experience minimum. No junior developers or offshore outsourcing.'
    },
    {
      icon: Zap,
      title: 'Fast Onboarding',
      description: 'Developers ready to contribute within 1-2 weeks, not months.'
    },
    {
      icon: CheckCircle2,
      title: 'Pre-Vetted Talent',
      description: 'Only top 3% of applicants pass our technical screening process.'
    },
    {
      icon: Globe,
      title: 'Proven Track Record',
      description: '200+ successful AI implementations across healthcare, finance, manufacturing.'
    },
    {
      icon: Clock,
      title: 'Flexible Engagement',
      description: 'Scale up or down based on project needs. No long-term lock-in.'
    },
    {
      icon: TrendingUp,
      title: '99% Satisfaction',
      description: 'Highest client satisfaction in the industry with sustained partnerships.'
    }
  ];

  const techStack = [
    {
      category: 'AI Frameworks',
      technologies: ['TensorFlow', 'PyTorch', 'scikit-learn', 'Keras', 'JAX']
    },
    {
      category: 'LLM & Gen AI',
      technologies: ['OpenAI GPT-4', 'Claude', 'LangChain', 'LlamaIndex', 'Hugging Face']
    },
    {
      category: 'Cloud Platforms',
      technologies: ['AWS Bedrock', 'Azure OpenAI', 'Google Vertex AI', 'AWS SageMaker']
    },
    {
      category: 'Vector Databases',
      technologies: ['Pinecone', 'Weaviate', 'Chroma', 'Qdrant', 'Milvus']
    },
    {
      category: 'MLOps Tools',
      technologies: ['Kubernetes', 'Docker', 'MLflow', 'Kubeflow', 'Weights & Biases']
    },
    {
      category: 'Languages',
      technologies: ['Python', 'JavaScript/TypeScript', 'Rust', 'Go', 'SQL']
    }
  ];

  const successStories = [
    {
      industry: 'Healthcare',
      challenge: 'Needed AI developers to build diagnostic AI system',
      solution: 'Provided 5-person ML team including data engineers and MLOps specialists',
      result: '95% diagnostic accuracy, 6-week delivery, ongoing support',
      teamSize: '5 developers',
      duration: '6 weeks + ongoing'
    },
    {
      industry: 'E-commerce',
      challenge: 'Required LLM experts for AI-powered customer service chatbot',
      solution: 'Hired 3 LLM integration specialists with GPT-4 and RAG expertise',
      result: '70% reduction in support tickets, 4-week implementation',
      teamSize: '3 developers',
      duration: '4 weeks'
    },
    {
      industry: 'Manufacturing',
      challenge: 'Needed computer vision engineers for quality inspection AI',
      solution: 'Dedicated team of 4 CV engineers with production deployment experience',
      result: '98% defect detection, 5-week deployment, $2M annual savings',
      teamSize: '4 developers',
      duration: '5 weeks'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Hire AI Developers | Senior ML Engineers & AI Specialists | Agility</title>
        <meta
          name="description"
          content="Hire pre-vetted senior AI developers and ML engineers. 5+ years experience. Fast onboarding in 1-2 weeks. Dedicated teams, staff augmentation, or project-based. OpenAI, Claude, TensorFlow, PyTorch experts. 200+ successful projects."
        />
        <meta
          name="keywords"
          content="hire AI developers, hire machine learning engineers, hire AI engineers, AI development team, hire ML developers, AI staff augmentation, dedicated AI team, hire OpenAI developers, hire LLM developers, AI talent acquisition"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/hire-ai-developers" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Hire AI Developers | Senior ML Engineers & AI Specialists" />
        <meta property="og:description" content="Hire pre-vetted senior AI developers and ML engineers. Fast onboarding in 1-2 weeks. OpenAI, Claude, TensorFlow, PyTorch experts." />
        <meta property="og:url" content="https://www.agilitytech.ai/hire-ai-developers" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />

        {/* Schema.org Service markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Hire AI Developers",
            "description": "Hire pre-vetted senior AI developers and machine learning engineers with 5+ years experience. Fast onboarding, flexible engagement models.",
            "provider": {
              "@type": "Organization",
              "name": "Agility",
              "url": "https://www.agilitytech.ai"
            },
            "serviceType": "AI Development Staffing",
            "areaServed": "Worldwide",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.95",
              "reviewCount": "200",
              "bestRating": "5"
            },
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock",
              "price": "Contact for pricing",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <Users className="w-4 h-4 text-teal-400" />
            <span className="text-teal-300 text-sm font-medium">200+ Senior AI Developers Available</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Hire <span className="text-teal-400">Senior AI Developers</span> in 1-2 Weeks
          </h1>

          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Pre-vetted machine learning engineers, LLM specialists, and AI architects with 5+ years experience.
            No junior developers. No offshore outsourcing. Just senior engineers who deliver results.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Hire AI Developers
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all"
            >
              View Success Stories
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">5+</div>
              <div className="text-gray-400">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">1-2</div>
              <div className="text-gray-400">Weeks Onboarding</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">Top 3%</div>
              <div className="text-gray-400">Talent Accepted</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">99%</div>
              <div className="text-gray-400">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hire <span className="text-teal-600">Expert AI Developers</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access specialized AI talent across all domains of artificial intelligence and machine learning
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {expertiseAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <div key={index} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{area.title}</h3>
                  <p className="text-gray-600 mb-6">{area.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {area.skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Flexible <span className="text-teal-600">Engagement Models</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the hiring model that best fits your project needs and timeline
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {engagementModels.map((model, index) => {
              const Icon = model.icon;
              return (
                <div key={index} className="fade-in-up bg-white rounded-xl p-8 hover:shadow-xl transition-all border-2 border-gray-200 hover:border-teal-500">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{model.title}</h3>
                  <p className="text-gray-600 mb-6">{model.description}</p>

                  <ul className="space-y-3 mb-6">
                    {model.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-6 border-t border-gray-200">
                    <div className="text-sm font-semibold text-gray-500 mb-2">BEST FOR:</div>
                    <div className="text-gray-900">{model.bestFor}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hiring Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Simple <span className="text-teal-600">Hiring Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From requirement sharing to project kickoff in as little as 1-2 weeks
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {hiringProcess.map((step, index) => (
              <div key={index} className="fade-in-up relative">
                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                    <Clock className="w-4 h-4" />
                    {step.duration}
                  </div>
                </div>
                {index < hiringProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-teal-200 -ml-4"></div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Get Started Today
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why Hire AI Developers <span className="text-teal-400">From Agility?</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We're not a staffing agency. We're AI experts who build world-class teams.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="fade-in-up bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-teal-500/50 transition-all">
                  <Icon className="w-12 h-12 text-teal-400 mb-6" />
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Developers' <span className="text-teal-600">Technology Expertise</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hire developers proficient in the latest AI frameworks, tools, and platforms
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {techStack.map((category, index) => (
              <div key={index} className="fade-in-up bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{category.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {category.technologies.map((tech, idx) => (
                    <span key={idx} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Client <span className="text-teal-600">Success Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real projects where our hired AI developers delivered exceptional results
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div key={index} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200">
                <div className="text-sm font-semibold text-teal-600 mb-4">{story.industry}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Challenge</h3>
                <p className="text-gray-700 mb-4">{story.challenge}</p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">Solution</h3>
                <p className="text-gray-700 mb-4">{story.solution}</p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">Results</h3>
                <p className="text-gray-700 mb-6">{story.result}</p>

                <div className="flex items-center justify-between pt-6 border-t border-teal-200">
                  <div>
                    <div className="text-sm text-gray-500">Team Size</div>
                    <div className="font-semibold text-gray-900">{story.teamSize}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Duration</div>
                    <div className="font-semibold text-gray-900">{story.duration}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold"
            >
              View All Case Studies
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Hire Senior AI Developers?
          </h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            Get matched with pre-vetted AI developers in 48 hours. Start building tomorrow.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Hire AI Developers Now
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-teal-50">
            or call us at <span className="font-semibold">+91-7041402034</span>
          </p>
        </div>
      </section>
    </>
  );
};

export default HireAIDevelopers;
